import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { Map as GoogleMap } from '@vis.gl/react-google-maps';
import { AbilityContext } from '../../../../components/ability/can';
import { ISiteLocation } from '../../../../domain/site/interface';
import { CarbonIntensityMapLegends } from './CarbonIntensityMapLegends';
import { LocalMWMapLegends } from './LocalMWMapLegends';
import { useAuth } from '../../../../domain/auth/useAuth';
import { ClusteredMarkers } from './ClusteredMarkers';
import { SelectedSiteInfo } from './SelectedSiteInfo';
import { useSiteMapStore } from './useSiteMapStore';

/**
 * this ID is created at Google Console,
 * the map style is completely configured at Google Console for this ID
 */
const GOOGLE_MAP_ID = '89a7ed738fbaaf998be29fd6';

// Isolated component so that selecting a marker only re-renders this subtree,
// not ClusteredMarkers and its children.
function SelectedSitePopup({ siteLocations }: { siteLocations: ISiteLocation[] }) {
    const selectedLocationKey = useSiteMapStore(state => state.selectedLocationKey);
    const setSelectedLocationKey = useSiteMapStore(state => state.setSelectedLocationKey);

    const selectedSites = useMemo(
        () => (selectedLocationKey ? siteLocations.filter(s => `${s.site_lat}_${s.site_long}` === selectedLocationKey) : []),
        [siteLocations, selectedLocationKey]
    );

    if (!selectedSites.length) return null;
    return <SelectedSiteInfo sites={selectedSites} onClose={() => setSelectedLocationKey(null)} />;
}

interface ISitesMapProps {
    siteLocations: ISiteLocation[];
}

export function SitesMap(props: ISitesMapProps) {
    const auth = useAuth();
    const ability = useContext(AbilityContext);
    const setSelectedLocationKey = useSiteMapStore(state => state.setSelectedLocationKey);
    const setMapLoaded = useSiteMapStore(state => state.setMapLoaded);

    // defaultCenter is only applied on initial mount — read once without subscribing to future changes
    const [initialMapLocation] = useState(() => useSiteMapStore.getState().mapLocation);

    const isUtilityCustomer = auth?.user?.isUtilityCustomer();
    const mapContainerStyle = useMemo(
        () => ({
            height: isUtilityCustomer ? '617px' : '575px',
            width: '100%',
        }),
        [isUtilityCustomer]
    );

    const resetSelectedLocation = useCallback(() => setSelectedLocationKey(null), [setSelectedLocationKey]);
    const onMapIdle = useCallback(() => setMapLoaded(), [setMapLoaded]);

    return (
        <div>
            <GoogleMap
                mapId={GOOGLE_MAP_ID}
                defaultCenter={initialMapLocation}
                defaultZoom={4}
                maxZoom={19}
                style={mapContainerStyle}
                onClick={resetSelectedLocation}
                onDragstart={resetSelectedLocation}
                onIdle={onMapIdle}
                reuseMaps={true}
            >
                {props.siteLocations && <ClusteredMarkers sites={props.siteLocations} />}
                <SelectedSitePopup siteLocations={props.siteLocations} />
            </GoogleMap>

            <LocalMWMapLegends />

            {ability.can('read', 'carbon-intensity') && <CarbonIntensityMapLegends />}
        </div>
    );
}

function propsAreEqual(prev: ISitesMapProps, next: ISitesMapProps) {
    return prev.siteLocations === next.siteLocations;
}

export const MemoizedSitesMap = memo(SitesMap, propsAreEqual);
